/*
 * PlaneStratum — one horizontal "layer" of the substrate.
 *
 * The five planes ARE the architecture, so the plane-health view stacks them
 * top-to-bottom as strata of one slab (Trust → Transport → Catalog →
 * Observability → Consent), NOT as a grid of detached cards.
 *
 * Four honest visual states, branched on `state.kind`:
 *
 *   live     → a RAISED, LIT teal band. Full opacity, a lit edge + lift, the
 *              live "✓ ONLINE" VerifyBadge and inline live metrics. The only
 *              plane serving real business data in this deployment (Transport).
 *
 *   deployed → a CALM-BUT-POSITIVE indigo-tinted band for a plane whose
 *              health stub is UP (container deployed, /healthz 200) but whose
 *              business read-API is NOT wired yet. Distinct from dormant (which
 *              means the service is unreachable) and from live (which means real
 *              business data). It carries the WORDS "deployed", "health 200" and
 *              "business API pending" — never colour alone, and it NEVER implies
 *              live business data. Collapsed by default; discloses the endpoints
 *              the business API WILL BIND TO once wired.
 *
 *   dormant  → a DIM, recessed indigo/neutral band for a plane whose backing
 *              service is not reachable yet. CALM, never red — this is an
 *              expected pre-pilot state, not a failure. Collapsed by default;
 *              a native <details>/<summary> discloses the endpoints it WILL
 *              BIND TO (keyboard-operable, 44px target, aria-expanded built in).
 *
 *   failure  → a LOUD RED band for a plane that responded UNEXPECTEDLY — a real
 *              failure. Carries an alarm glyph + the word "FAILURE" + red, so
 *              the signal is never colour-alone (WCAG 1.4.1 / 2.2 AA).
 *
 * Colour == meaning here, by token (tokens.css): teal = live/verified,
 * indigo = structure (dormant AND deployed-but-pending), red = genuine
 * failure ONLY. The deployed/dormant pair stays inside the indigo family on
 * purpose — neither reaches the lit teal reserved for real live data.
 */

import type { ReactNode } from "react";
import { SpecRef } from "./SpecRef";

/** A single inline live metric for the lit Transport band. */
export interface LiveMetric {
  label: string;
  value: string;
  /** Open the ProvabilityDrawer for this metric's /v1/metrics line. */
  onInspect?: () => void;
}

export type StratumState =
  | {
      kind: "live";
      /** The header status badge (caller passes a wired VerifyBadge). */
      badge: ReactNode;
      /** Inline metrics shown on the band, each inspectable. */
      metrics: LiveMetric[];
    }
  | {
      kind: "deployed";
      /** The service whose health stub is up (e.g. "trust-log"). */
      service: string;
      /** Health-probe latency in ms, if measured (shown as evidence). */
      latencyMs?: number;
      /** Business endpoints the plane will bind to once the read-API is wired. */
      endpoints: string[];
    }
  | {
      kind: "dormant";
      /** The service this plane is awaiting (e.g. "trust-log"). */
      service: string;
      /** Endpoints the plane will bind to once its service is up. */
      endpoints: string[];
    }
  | {
      kind: "failure";
      /** Why the probe is loud — it responded when it shouldn't have. */
      message: string;
      /** Endpoints involved, for context. */
      endpoints: string[];
    };

interface PlaneStratumProps {
  /** Plane name, e.g. "Trust". */
  name: string;
  /** Plane + section refs, e.g. "Plane III · §15–§23". */
  refs: string;
  state: StratumState;
}

export function PlaneStratum({ name, refs, state }: PlaneStratumProps) {
  if (state.kind === "live") return <LiveStratum name={name} refs={refs} state={state} />;
  if (state.kind === "failure") return <FailureStratum name={name} refs={refs} state={state} />;
  if (state.kind === "deployed") return <DeployedStratum name={name} refs={refs} state={state} />;
  return <DormantStratum name={name} refs={refs} state={state} />;
}

/* ---------------------------------------------------------------------- *
 * LIVE — raised, lit teal band.                                          *
 * ---------------------------------------------------------------------- */

function LiveStratum({
  name,
  refs,
  state,
}: {
  name: string;
  refs: string;
  state: Extract<StratumState, { kind: "live" }>;
}) {
  return (
    <div className="stratum stratum--live" role="status">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--space-4)",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <span
            aria-hidden="true"
            className="stratum__dot"
            style={{
              background: "var(--teal)",
              boxShadow: "0 0 0 3px var(--teal-dim)",
            }}
          />
          <div>
            <h3 style={{ fontSize: "18px", margin: 0 }}>{name}</h3>
            <div className="mono" style={{ fontSize: "10px", color: "var(--teal)" }}>
              <SpecRef text={refs} /> · LIVE
            </div>
          </div>
        </div>
        {state.badge}
      </div>

      {/* Inline live metrics — each links to the /v1/metrics line it came from. */}
      <ul
        style={{
          listStyle: "none",
          margin: "var(--space-3) 0 0",
          padding: 0,
          display: "flex",
          gap: "var(--space-5)",
          flexWrap: "wrap",
        }}
      >
        {state.metrics.map((m) => (
          <li
            key={m.label}
            style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}
          >
            <span
              className="mono"
              style={{
                fontSize: "10px",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--fg-faint)",
              }}
            >
              {m.label}
            </span>
            {m.onInspect ? (
              <button
                type="button"
                onClick={m.onInspect}
                title="Inspect source line"
                style={{
                  alignSelf: "flex-start",
                  background: "none",
                  border: "none",
                  padding: 0,
                  fontFamily: "var(--font-mono)",
                  fontSize: "16px",
                  color: "var(--fg)",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                {m.value}{" "}
                <span aria-hidden="true" style={{ fontSize: "11px", color: "var(--fg-faint)" }}>
                  ↗
                </span>
              </button>
            ) : (
              <span className="mono" style={{ fontSize: "16px", color: "var(--fg)" }}>
                {m.value}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * DEPLOYED — calm-but-positive indigo band. Health stub up, business API  *
 * pending. NOT the lit teal of live data; NOT the recessed dim of dormant.*
 * ---------------------------------------------------------------------- */

function DeployedStratum({
  name,
  refs,
  state,
}: {
  name: string;
  refs: string;
  state: Extract<StratumState, { kind: "deployed" }>;
}) {
  // role="status" (polite) — this is a positive, non-urgent state. The signal
  // is carried by WORDS ("deployed", "health 200", "business API pending"), so
  // it is never colour-alone (WCAG 1.4.1). Indigo-tinted, never the lit teal.
  return (
    <details className="stratum stratum--deployed" role="status">
      <summary
        className="stratum__summary"
        aria-label={`${name} plane — ${state.service} deployed, health 200, business API pending. Expand to see the business endpoints it will bind to.`}
      >
        <span
          aria-hidden="true"
          className="stratum__dot"
          // brighter-than-dormant indigo dot with a soft ring — positive, calm,
          // but deliberately NOT the teal reserved for real live data.
          style={{
            background: "rgb(var(--indigo-rgb))",
            boxShadow: "0 0 0 3px var(--indigo-dim)",
          }}
        />
        <div style={{ minWidth: 0 }}>
          <span style={{ fontSize: "15px", color: "var(--fg)" }}>{name}</span>{" "}
          <span className="mono" style={{ fontSize: "10px", color: "var(--fg-faint)" }}>
            <SpecRef text={refs} />
          </span>
          <div
            className="mono"
            style={{
              fontSize: "12px",
              color: "var(--indigo)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {/* WORD-bearing chips — signal is text, not colour. */}
            <span
              className="stratum__chip"
              style={{
                color: "var(--indigo)",
                borderColor: "rgba(var(--indigo-rgb), 0.4)",
                background: "var(--indigo-dim)",
              }}
            >
              <span aria-hidden="true">▣</span> deployed
            </span>
            <span style={{ color: "var(--fg-muted)" }}>
              health 200
              {state.latencyMs !== undefined ? ` · ${state.latencyMs} ms` : ""}
            </span>
            <span style={{ color: "var(--fg-faint)" }}>· business API pending</span>
          </div>
        </div>
        <span className="stratum__caret mono" aria-hidden="true">
          ▸
        </span>
      </summary>

      <div style={{ marginTop: "var(--space-3)", paddingLeft: "22px" }}>
        <div
          className="mono"
          style={{
            fontSize: "11px",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "var(--fg-faint)",
            marginBottom: "6px",
          }}
        >
          Business API will bind to
        </div>
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          {state.endpoints.map((e) => (
            <li key={e} className="mono-surface" style={{ color: "var(--fg-muted)" }}>
              {e}
            </li>
          ))}
        </ul>
        <p
          className="mono"
          style={{
            margin: "var(--space-3) 0 0",
            fontSize: "11px",
            color: "var(--fg-faint)",
            maxWidth: "60ch",
          }}
        >
          Service container is up and reporting healthy. No business data is
          served yet — the read-API above is a future increment.
        </p>
      </div>
    </details>
  );
}

/* ---------------------------------------------------------------------- *
 * DORMANT — dim, recessed, CALM indigo band; collapsible disclosure.     *
 * ---------------------------------------------------------------------- */

function DormantStratum({
  name,
  refs,
  state,
}: {
  name: string;
  refs: string;
  state: Extract<StratumState, { kind: "dormant" }>;
}) {
  // Native <details> gives us aria-expanded + keyboard operation for free.
  return (
    <details className="stratum stratum--dormant">
      <summary
        className="stratum__summary"
        aria-label={`${name} plane — awaiting ${state.service}. Expand to see the endpoints it will bind to.`}
      >
        <span
          aria-hidden="true"
          className="stratum__dot"
          // dim indigo dot — calm, NOT alarm-red
          style={{ background: "rgba(var(--indigo-rgb), 0.55)" }}
        />
        <div style={{ minWidth: 0 }}>
          <span style={{ fontSize: "15px", color: "var(--fg-muted)" }}>{name}</span>{" "}
          <span className="mono" style={{ fontSize: "10px", color: "var(--fg-faint)" }}>
            <SpecRef text={refs} />
          </span>
          <div className="mono" style={{ fontSize: "12px", color: "var(--fg-faint)" }}>
            <span aria-hidden="true">◷</span> awaiting ·{" "}
            <span style={{ color: "var(--fg-muted)" }}>
              <SpecRef text={state.service} />
            </span>
          </div>
        </div>
        <span className="stratum__caret mono" aria-hidden="true">
          ▸
        </span>
      </summary>

      <div style={{ marginTop: "var(--space-3)", paddingLeft: "22px" }}>
        <div
          className="mono"
          style={{
            fontSize: "11px",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "var(--fg-faint)",
            marginBottom: "6px",
          }}
        >
          Will bind to
        </div>
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          {state.endpoints.map((e) => (
            <li key={e} className="mono-surface" style={{ color: "var(--fg-muted)" }}>
              {e}
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}

/* ---------------------------------------------------------------------- *
 * FAILURE — LOUD red band. A plane that responded unexpectedly.          *
 * ---------------------------------------------------------------------- */

function FailureStratum({
  name,
  refs,
  state,
}: {
  name: string;
  refs: string;
  state: Extract<StratumState, { kind: "failure" }>;
}) {
  return (
    <div className="stratum stratum--failure" role="alert">
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
        <span
          aria-hidden="true"
          className="stratum__dot"
          style={{ background: "var(--red)", boxShadow: "0 0 0 4px var(--red-dim)" }}
        />
        <div style={{ minWidth: 0 }}>
          <span style={{ fontSize: "16px", color: "var(--red)", fontWeight: 600 }}>
            {name}
          </span>{" "}
          <span className="mono" style={{ fontSize: "10px", color: "var(--fg-faint)" }}>
            <SpecRef text={refs} />
          </span>
          <div
            className="mono"
            style={{ fontSize: "12px", color: "var(--red)", fontWeight: 600 }}
          >
            {/* signal is glyph + WORD + colour — never colour alone */}
            <span aria-hidden="true">▲</span> FAILURE — responded unexpectedly
          </div>
        </div>
      </div>
      <p style={{ color: "var(--fg-muted)", margin: "var(--space-3) 0 0", maxWidth: "60ch" }}>
        {state.message}
      </p>
      {state.endpoints.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: "var(--space-3) 0 0",
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          {state.endpoints.map((e) => (
            <li key={e} className="mono-surface" style={{ color: "var(--fg-muted)" }}>
              {e}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
